import React, { useState, useEffect, useContext, useRef } from "react";
import { Modal } from "react-bootstrap";
import {
  Search,
  Plus,
  MessageSquare,
  Clock,
  X,
  ChevronDown,
  Send,
} from "lucide-react";

import axios from "axios";
import Swal from "sweetalert2";

import "../../../assets/css/gcstaff/myDisputes/Disputes.css";
import AppContext from "../../../context/AppContext";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

const categoryOptions = [
  "Wrong Fine",
  "Book Not Returned",
  "Reservation Issue",
  "Account Issue",
  "Book Issue",
  "Lost Book",
  "Damaged Book",
  "Other",
];

/* ══════════════════════════════════════
   Chat Modal — token/teacherId from AppContext
══════════════════════════════════════ */
const ChatModal = ({ item, onClose, baseUrl, teacherId, authToken }) => {
  const [visible, setVisible] = useState(false);
  const [msg, setMsg] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setTimeout(() => setVisible(true), 30);

    fetchMessages(true); // pehli baar loader dikhao

    const interval = setInterval(() => {
      fetchMessages(false); // baad mein silently refresh
    }, 4000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const fetchMessages = (showLoader = false) => {
    if (showLoader) setLoading(true);

    const formData = new FormData();
    formData.append("lbteacher_id", teacherId);
    formData.append("lbdispute_id", item.id);
    formData.append("type", "dispute");

    axios
      .post(`${baseUrl}/teacher/chat/fetchAllChats`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success === true) {
          setMessages(res.data.chats || []);
        }
      })
      .catch((err) => {
        console.log("error", err);
      })
      .finally(() => {
        if (showLoader) setLoading(false);
      });
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 220);
  };

  const send = () => {
    if (!msg.trim() || sending) return;

    setSending(true);

    const formData = new FormData();
    formData.append("lbteacher_id", teacherId);
    formData.append("lbdispute_id", item.id);
    formData.append("type", "dispute");
    formData.append("message", msg.trim());

    axios
      .post(`${baseUrl}/teacher/chat/store`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const sentMsg = { ...res.data.chat, sender: "teacher" };
        setMessages((p) => [...p, sentMsg]);
        setMsg("");
      })
      .catch((error) => {
        console.log("Send chat error:", error.response?.data || error);
        Swal.fire("Error", "Message could not be sent", "error");
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <div
      className={`gcDsp-chat-backdrop ${visible ? "gcDsp-chat-backdrop--in" : ""}`}
      onClick={handleClose}
    >
      <div
        className={`gcDsp-chat-modal ${visible ? "gcDsp-chat-modal--in" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gcDsp-chat-header">
          <div>
            <h5 className="gcDsp-chat-title">Dispute Chat</h5>
            <p className="gcDsp-chat-sub">#{item.id} • {item.subject}</p>
          </div>
          <button className="gcDsp-chat-close" onClick={handleClose}>
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        <div className="gcDsp-chat-body">
          {loading && <p className="gcDsp-chat-empty">Loading messages...</p>}

          {!loading && messages.length === 0 && (
            <p className="gcDsp-chat-empty">
              No messages yet. Send the first one to start the conversation.
            </p>
          )}

          {!loading &&
            messages.map((m) => {
              const senderValue = (m.sender || "").toString().trim().toLowerCase();
              const isMine = senderValue === "teacher";
              return (
                <div
                  key={m.id}
                  className={`gcDsp-chat-msg ${isMine ? "gcDsp-chat-msg--mine" : "gcDsp-chat-msg--theirs"}`}
                >
                  <p className="gcDsp-chat-msg-text">{m.message}</p>
                  <span className="gcDsp-chat-msg-time">
                    {new Date(m.created_at).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              );
            })}
        </div>

        <div className="gcDsp-chat-input-row">
          <input
            className="gcDsp-chat-input"
            placeholder="Type a message..."
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
          />
          <button className="gcDsp-chat-send" onClick={send} disabled={sending}>
            <Send size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════
   Create Dispute Modal (Staff)
══════════════════════════════════════ */
const CreateDisputeModal = ({ show, onClose, onSubmit, bookings, bookingsLoading }) => {
  const [subject, setSubject] = useState("");
  const [book, setBook] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");

  const handleSubmit = () => {
    if (!subject.trim() || !description.trim()) {
      Swal.fire("Wait", "Please fill required fields", "warning");
      return;
    }

    onSubmit({ subject, book, category, description });

    setSubject("");
    setBook("");
    setCategory("");
    setDescription("");

    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="gcstudent-dispute-modal-wrapper"
      backdropClassName="gcstudent-dispute-backdrop"
    >
      <div className="gcstudent-dispute-modal gcstudent-dispute-modal--form">
        <div className="gcstudent-dispute-modal__header">
          <span className="gcstudent-dispute-modal__id">
            Create New Staff Dispute
          </span>
          <button className="gcstudent-dispute-modal__close" onClick={onClose}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="gcstudent-dsp-form">
          <div className="gcstudent-dsp-form__group">
            <label className="gcstudent-dsp-form__label">Subject</label>
            <input
              type="text"
              className="gcstudent-dsp-form__input"
              placeholder="Enter dispute subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="gcstudent-dsp-form__group">
            <label className="gcstudent-dsp-form__label">Related Book</label>
            <div className="gcstudent-dsp-form__select-wrap">
              <select
                className="gcstudent-dsp-form__select"
                value={book}
                onChange={(e) => setBook(e.target.value)}
                disabled={bookingsLoading}
              >
                <option value="">
                  {bookingsLoading
                    ? "Loading your books..."
                    : bookings.length === 0
                    ? "No reserved/issued books found"
                    : "Select book"}
                </option>
                {bookings.map((b) => (
                  <option key={b.id} value={b.lbbook_id}>
                    {b.lbbook?.title || `Book #${b.lbbook_id}`}
                    {b.status ? ` (${b.status})` : ""}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="gcstudent-dsp-form__chevron" />
            </div>
          </div>

          <div className="gcstudent-dsp-form__group">
            <label className="gcstudent-dsp-form__label">Category</label>
            <div className="gcstudent-dsp-form__select-wrap">
              <select
                className="gcstudent-dsp-form__select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Select type</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <ChevronDown size={14} className="gcstudent-dsp-form__chevron" />
            </div>
          </div>

          <div className="gcstudent-dsp-form__group">
            <label className="gcstudent-dsp-form__label">Description</label>
            <textarea
              className="gcstudent-dsp-form__textarea"
              placeholder="Describe your issue in detail..."
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="gcstudent-dsp-form__actions">
            <button className="gcstudent-dsp-form__submit" onClick={handleSubmit}>
              Submit Dispute
            </button>
            <button className="gcstudent-dsp-form__cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════
   Main Disputes Page (Staff)
   ✅ Student jaisi list design (gcTeacher- prefix)
   ✅ Chat sirf MessageSquare button click se khulta hai — row click nahi
══════════════════════════════════════ */
const ITEMS_PER_PAGE = 10; // yahan se page size control hoti hai

const Disputes = () => {
  const { baseUrl, loggedInUser, authToken } = useContext(AppContext);

  const [disputes, setDisputes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);     // initial API fetch loading
  const [viewLoading, setViewLoading] = useState(false); // page-switch / search loading (sirf visible rows k liye)
  const [chatItem, setChatItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // pagination state

  const [myBookings, setMyBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  const teacherId = loggedInUser?.id;

  // Pehli render par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  const fetchAllDisputes = (id) => {
    setLoading(true);

    const formData = new FormData();
    formData.append("lbteacher_id", id);

    axios
      .post(`${baseUrl}/teacher/disputes/fetchAllDisputes`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setDisputes(res.data.disputes || []);
      })
      .catch((error) => {
        console.log(error);
        Swal.fire("Error", "Failed to fetch staff disputes", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchMyBookings = () => {
    if (!loggedInUser?.id) return;

    setBookingsLoading(true);

    axios
      .post(
        `${baseUrl}/teacher/booking/loadMyBookings`,
        { teacher_id: loggedInUser.id },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        setMyBookings(res.data.success ? res.data.bookings : []);
      })
      .catch((err) => {
        console.log(err);
        setMyBookings([]);
      })
      .finally(() => {
        setBookingsLoading(false);
      });
  };

  const handleSubmit = (form) => {
    if (!loggedInUser?.id) {
      Swal.fire("Error", "Your session expired. Please log in again.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);
    formData.append("lbbook_id", form.book);
    formData.append("raisedby", "teacher");
    formData.append("subject", form.subject);
    formData.append("category", form.category);
    formData.append("description", form.description);

    axios
      .post(`${baseUrl}/teacher/disputes/add`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then(() => {
        Swal.fire("Success", "Staff dispute submitted", "success");
        fetchAllDisputes(loggedInUser.id);
      })
      .catch((error) => {
        console.log("Submit error:", error.response?.data || error);
        Swal.fire("Error", "Failed to submit dispute", "error");
      });
  };

  useEffect(() => {
    if (loggedInUser?.id) {
      fetchAllDisputes(loggedInUser.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser]);

  useEffect(() => {
    if (showCreate) {
      fetchMyBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate]);

  const filtered = disputes.filter(
    (d) =>
      d.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* ── Pagination slice (sirf current page ka data list mein jayega) ── */
  const paginatedDisputes = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Search change hone par page 1 pe reset ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  /* ── Sirf visible rows ki loading dikhayein:
     - Jab search query change ho
     - Jab pagination se dusre page par jayen
     Poori list ek sath "pop" nahi hogi, sirf jitne disputes is waqt
     screen par show hone hain unki hi loading chalegi. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentPage, searchQuery]);

  return (
    <div className="gcTeacher-disputes">
      <div className="gcTeacher-disputes__header">
        <h2>Staff Disputes</h2>
        <button className="gcTeacher-disputes__new-btn" onClick={() => setShowCreate(true)}>
          <Plus size={15} strokeWidth={2.5} />
          New Dispute
        </button>
      </div>

      <div className="gcTeacher-disputes__search-row">
        <input
          type="text"
          className="gcTeacher-disputes__search-input"
          placeholder="Search staff disputes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button className="gcTeacher-disputes__search-btn">
          <Search size={15} strokeWidth={2.2} />
        </button>
      </div>

      {/* ── Initial Loading (poora page pehli baar load ho raha hai) ── */}
      {loading && <Loader message="Disputes loading, Please wait..." />}

      {/* ── List ── */}
      {!loading && (
        <>
          {viewLoading ? (
            /* ── Page/search switch loading — sirf list ki, baaki page affect nahi hota ── */
            <div className="gcTeacher-disputes__list">
              <Loader message="Loading disputes..." />
            </div>
          ) : (
            <div className="gcTeacher-disputes__list">
              {paginatedDisputes.map((dispute, i) => (
                <div
                  key={dispute.id}
                  className="gcTeacher-dsp-row"
                  style={{ "--row-index": i }}
                >
                  <div className="gcTeacher-dsp-row__left">
                    <span className="gcTeacher-dsp-row__icon gcTeacher-dsp-row__icon--open">
                      <Clock size={14} strokeWidth={2.2} />
                    </span>
                    <div>
                      <p className="gcTeacher-dsp-row__title">{dispute.subject}</p>
                      <p className="gcTeacher-dsp-row__meta">
                        <span className="gcTeacher-dsp-row__idtag">#{dispute.id}</span>
                        <span className="gcTeacher-dsp-row__sep">•</span>
                        <span>{dispute.category}</span>
                        <span className="gcTeacher-dsp-row__sep">•</span>
                        <Clock size={11} strokeWidth={2} className="gcTeacher-dsp-row__meta-clock" />
                        <span>
                          {dispute.created_at
                            ? new Date(dispute.created_at).toLocaleDateString()
                            : "N/A"}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="gcTeacher-dsp-row__right">
                    <span
                      className={`gcTeacher-dsp-badge gcTeacher-dsp-badge--${(dispute.status || "open").toLowerCase()}`}
                    >
                      {dispute.status || "Open"}
                    </span>
                    <button
                      className="gcTeacher-dsp-row__chat-btn"
                      onClick={() => setChatItem(dispute)}
                      aria-label="Open chat"
                    >
                      <MessageSquare size={16} strokeWidth={1.8} />
                    </button>
                  </div>
                </div>
              ))}

              {filtered.length === 0 && (
                <div className="gcTeacher-disputes__empty">
                  <MessageSquare size={36} strokeWidth={1.2} />
                  <p>No disputes found.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {chatItem && (
        <ChatModal
          item={chatItem}
          onClose={() => setChatItem(null)}
          baseUrl={baseUrl}
          teacherId={teacherId}
          authToken={authToken}
        />
      )}

      <CreateDisputeModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleSubmit}
        bookings={myBookings}
        bookingsLoading={bookingsLoading}
      />
    </div>
  );
};

export default Disputes;